import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import logo from '../assets/logo.png';

const links = [
  { to: '/', label: 'Home' },
  { to: '/#services', label: 'Services' },
  { to: '/#process', label: 'Process' },
  { to: '/#testimonials', label: 'Reviews' },
  { to: '/pricing', label: 'Pricing' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const location = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAnchor = (e, href) => {
    if (href.startsWith('/#')) {
      if (location.pathname === '/') {
        e.preventDefault();
        const id = href.replace('/#', '');
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 24px',
        background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.08)' : '1px solid transparent',
        transition: 'all 0.4s ease',
      }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto', display: 'flex', alignItems: 'center', height: '160px', gap: '24px', justifyContent: width <= 768 ? 'space-between' : 'flex-start' }}>

          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="WebWalay" style={{ width: '100px', height: '100px', display: 'block', objectFit: 'contain' }} />
          </Link>

          {/* Desktop nav */}
          <div style={{ display: width > 768 ? 'flex' : 'none', gap: '4px', flex: 1, justifyContent: 'center' }}>
            {links.map(l => (
              <Link key={l.to} to={l.to}
                onClick={e => handleAnchor(e, l.to)}
                style={{
                  padding: '7px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                  color: location.pathname === l.to ? 'var(--lime-dark)' : 'var(--text2)',
                  textDecoration: 'none', transition: 'all 0.2s',
                  background: location.pathname === l.to ? 'rgba(120,81,160,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = location.pathname === l.to ? 'var(--lime-dark)' : 'var(--text2)'; }}
              >{l.label}</Link>
            ))}
          </div>

          {/* CTA */}
          <Link to="/pricing" className="btn-lime" style={{ fontSize: '13px', padding: '10px 20px', flexShrink: 0, display: width > 768 ? 'inline-block' : 'none' }}>
            Start Today →
          </Link>

          {/* Mobile */}
          <button onClick={() => setMenuOpen(v => !v)}
            style={{ display: width <= 768 ? 'block' : 'none', background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer' }}
            className="mobile-btn">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99,
          background: 'rgba(255,255,255,0.98)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '28px',
          animation: 'fadeIn 0.2s ease',
        }}>
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)}
              style={{ fontFamily: 'Syne,sans-serif', fontSize: '28px', fontWeight: '700', color: 'var(--text)', textDecoration: 'none' }}>
              {l.label}
            </Link>
          ))}
          <Link to="/pricing" className="btn-lime" onClick={() => setMenuOpen(false)} style={{ marginTop: '12px', fontSize: '18px', padding: '12px 24px' }}>
            Start Today →
          </Link>
        </div>
      )}
    </>
  );
}